import React, { useMemo, useRef, useState } from 'react';
import { View, SectionList, Pressable, ActivityIndicator, StyleSheet, Alert } from 'react-native';
import { Text, TextInput } from '../ui/Text';
import { SafeAreaView } from 'react-native-safe-area-context';
import ReanimatedSwipeable, { SwipeableMethods } from 'react-native-gesture-handler/ReanimatedSwipeable';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useAuth } from '../auth';
import {
  useRooms, useInvites, useSyncState, useOpenGates, useIsAgent, partitionRooms, answerGate, GateChoice, acceptInvite, declineInvite,
  setHidden, setKept, leaveRoom, isHidden, senderName, localpart, canSetState, threadUnreadTotal, Room, Gate, Invite, joinWorkspace
} from '../matrix';
import { usePrefs } from '../prefs';
import { Avatar, RoomAvatar } from '../ui/Avatar';
import { ReviseSheet } from '../ui/ReviseSheet';
import { WorkspaceChips, useScope, pickScope, isPersonChat, ALL, PEOPLE, INBOX } from '../ui/WorkspaceSwitcher';
import { makeStyles, useTheme, radius } from '../theme';
import type { RootStackParamList } from '../navTypes';

type Props = NativeStackScreenProps<RootStackParamList, 'Home'>;
type Section =
  | { kind: 'gates'; title: string; data: Gate[] }
  | { kind: 'invites'; title: string; data: Invite[] }
  | { kind: 'rooms'; title: string; data: Room[]; spaceId?: string; admin?: boolean; collapsible?: boolean; expanded?: boolean;
      /** Home while workspaces sit on the rail: an empty list means the rooms are all in workspaces. */
      home?: boolean };

export default function HomeScreen({ navigation, activeRoomId, rail }: Props & {
  activeRoomId?: string;
  /** Desktop web: the workspaces are chosen on the rail beside this list, so it draws no chips of its own. */
  rail?: boolean;
}) {
  const theme = useTheme(); const s = useStyles();
  const { userId } = useAuth();
  const rooms = useRooms();
  const invites = useInvites();
  const gates = useOpenGates();
  const sync = useSyncState();
  const isAgent = useIsAgent();
  const { archiveDays, humanBadge } = usePrefs();
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const { scope, places, people, inbox, home, show: switcher, elsewhere } = useScope();
  const here = places.find((p) => p.id === scope);

  const open = (r: Room) => navigation.navigate('Chat', { roomId: r.id, title: r.name });

  const sections = useMemo<Section[]>(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      const hits = rooms.filter((r) => !r.isSpace && r.name.toLowerCase().includes(q));
      return [{ kind: 'rooms', title: `Results · ${hits.length}`, data: hits }];
    }
    const g = partitionRooms(rooms, archiveDays);
    const out: Section[] = [];
    if (scope === INBOX) return [{ kind: 'invites', title: invites.length ? `Invitations · ${invites.length}` : 'Inbox', data: invites }];
    if (scope === PEOPLE) {
      const mine = gates.filter((x) => people.ids.has(x.room.id));
      if (mine.length) out.push({ kind: 'gates', title: `Needs you · ${mine.length}`, data: mine });
      out.push({ kind: 'rooms', title: `People · ${people.rooms.length}`, data: people.rooms });
      return out;
    }
    if (here) {
      // One workspace: what waits for you in it, its rooms, and what was archived out of it. Searching (above)
      // always looks everywhere, because nobody remembers which workspace a room is in.
      const mine = gates.filter((x) => here.rooms.has(x.room.id));
      const sp = g.spaces.find((x) => x.space.id === here.id);
      const old = g.archived.filter((r) => here.rooms.has(r.id));
      if (mine.length) out.push({ kind: 'gates', title: `Needs you · ${mine.length}`, data: mine });
      if (sp) out.push({ kind: 'rooms', title: sp.space.name, data: sp.rooms, spaceId: sp.space.id, admin: canSetState(sp.space, 'm.space.child') });
      if (old.length) out.push({ kind: 'rooms', title: `Archived · ${old.length}`, data: showArchived ? old : [], collapsible: true, expanded: showArchived });
      return out;
    }
    // Home. What waits for you comes first, from every workspace: nobody should have to look through each one
    // for it. Then what sits outside the workspaces on the rail: direct chats with people, loose rooms, and what
    // was archived out of those. A workspace's rooms show inside that workspace; with a single workspace there
    // is no square for it, and its rooms stay here. Invitations live in the inbox; a line above the list says
    // when some wait.
    if (gates.length) out.push({ kind: 'gates', title: `Needs you · ${gates.length}`, data: gates });
    const railed = places.length > 0;
    if (!railed) for (const s of g.spaces) out.push({ kind: 'rooms', title: s.space.name, data: s.rooms, spaceId: s.space.id, admin: canSetState(s.space, 'm.space.child') });
    const chats = g.loose.filter((r) => isPersonChat(r, userId, isAgent));
    const other = g.loose.filter((r) => !isPersonChat(r, userId, isAgent));
    const old = railed ? g.archived.filter((r) => home.ids.has(r.id)) : g.archived;
    if (chats.length) out.push({ kind: 'rooms', title: 'People', data: chats });
    if (other.length || railed || !out.length) out.push({ kind: 'rooms', title: (!railed && g.spaces.length) || chats.length ? 'Other rooms' : 'Rooms', data: other, home: railed });
    if (old.length) out.push({ kind: 'rooms', title: `Archived · ${old.length}`, data: showArchived ? old : [], collapsible: true, expanded: showArchived });
    return out;
  }, [rooms, invites, gates, query, showArchived, archiveDays, here, scope, places, people, home, userId, isAgent]);

  // Swipe: in the main list a swipe archives; in Archived it brings the room back
  // (un-hides it, and pins it with m.favourite so it isn't auto-archived again for being idle).
  const archive = (r: Room) => setHidden(r.id, true).catch((e) => Alert.alert('Could not archive', e?.message));
  const unarchive = (r: Room) => (isHidden(r) ? setHidden(r.id, false) : setKept(r.id, true)).catch((e) => Alert.alert('Could not unarchive', e?.message));

  const roomActions = (r: Room) => {
    const hidden = isHidden(r);
    Alert.alert(r.name, undefined, [
      { text: hidden ? 'Unarchive' : 'Archive', onPress: () => (hidden ? unarchive(r) : archive(r)) },
      { text: 'Leave room', style: 'destructive', onPress: () => Alert.alert('Leave room?', `You'll stop receiving messages from "${r.name}".`, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Leave', style: 'destructive', onPress: () => leaveRoom(r.id).catch((e) => Alert.alert('Could not leave', e?.message)) },
      ]) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  return (
    <SafeAreaView style={s.root} edges={['top', 'left', 'right']}>
      <View style={s.header}>
        <View style={{ flex: 1 }}>
          <Text style={s.hTitle} numberOfLines={1}>helm</Text>
          <Text style={s.hSub} numberOfLines={1}>{userId ? `@${localpart(userId)}` : ''}</Text>
        </View>
        <Pressable onPress={() => navigation.navigate('NewChat')} hitSlop={10} style={s.newBtn} accessibilityRole="button" accessibilityLabel="New conversation"><Text style={s.newBtnText}>＋ New</Text></Pressable>
        <Pressable onPress={() => navigation.navigate('Settings')} hitSlop={10} style={s.gear} accessibilityRole="button" accessibilityLabel="Settings"><Text style={s.gearText}>⚙</Text></Pressable>
      </View>
      <View style={s.searchWrap}>
        <TextInput
          style={s.search} value={query} onChangeText={setQuery} placeholder="Search rooms" placeholderTextColor={theme.faint}
          autoCapitalize="none" autoCorrect={false} clearButtonMode="while-editing"
        />
      </View>

      {!rail && <WorkspaceChips />}
      {sync === 'error' && <View style={s.banner}><Text style={s.bannerText}>Reconnecting…</Text></View>}

      {sync === 'initial' && rooms.length === 0 ? (
        <View style={s.center}>
          <ActivityIndicator color={theme.accent} />
          <Text style={s.empty}>Syncing your rooms…</Text>
        </View>
      ) : (
        <SectionList
          sections={sections as any}
          keyExtractor={(item: any, i) => (item.event?.id || item.id || String(i))}
          contentContainerStyle={{ padding: 12, paddingBottom: 24 }}
          stickySectionHeadersEnabled={false}
          keyboardShouldPersistTaps="handled"
          // Inside one workspace, never hide that something waits in another.
          ListHeaderComponent={!switcher || query.trim() ? null : scope === ALL ? (inbox > 0 ? (
            <Pressable onPress={() => pickScope(INBOX)} style={({ pressed }) => [s.elsewhere, pressed && { opacity: 0.7 }]} accessibilityRole="button">
              <Text style={s.elsewhereText} numberOfLines={2}>{inbox === 1 ? '1 invitation is waiting' : `${inbox} invitations are waiting`}</Text>
              <Text style={[s.manage, { flexShrink: 0 }]} numberOfLines={1}>Open inbox ›</Text>
            </Pressable>
          ) : null) : elsewhere > 0 ? (
            <Pressable onPress={() => pickScope(ALL)} style={({ pressed }) => [s.elsewhere, pressed && { opacity: 0.7 }]} accessibilityRole="button">
              <Text style={s.elsewhereText} numberOfLines={2}>{elsewhere} more waiting elsewhere</Text>
              <Text style={[s.manage, { flexShrink: 0 }]} numberOfLines={1}>Show all ›</Text>
            </Pressable>
          ) : null}
          renderSectionHeader={({ section }: any) => (
            <Pressable
              disabled={!section.collapsible && !section.spaceId}
              onPress={() => (section.spaceId ? navigation.navigate('Workspace', { spaceId: section.spaceId }) : setShowArchived((v) => !v))}
              style={s.sectionRow}
            >
              <Text style={s.section} numberOfLines={1}>{section.title}</Text>
              {section.collapsible && <Text style={s.chev}>{section.expanded ? '▾' : '▸'}</Text>}
              {section.spaceId && <Text style={s.manage}>{section.admin ? 'Manage workspace ›' : 'Open workspace ›'}</Text>}
            </Pressable>
          )}
          renderItem={({ item, section }: any) => {
            if (section.kind === 'gates') return <GateCard gate={item} onOpen={() => open(item.room)} />;
            if (section.kind === 'invites') return <InviteRow invite={item} isAgent={isAgent} onAccepted={(r) => r && open(r)} />;
            const archived = !!section.collapsible || isHidden(item);
            return (
              <SwipeRow archived={archived} onSwipe={() => (archived ? unarchive(item) : archive(item))}>
                <RoomRow room={item} me={userId} isAgent={isAgent} humanBadge={humanBadge} active={activeRoomId === item.id} onPress={() => open(item)} onLongPress={() => roomActions(item)} />
              </SwipeRow>
            );
          }}
          renderSectionFooter={({ section }: any) =>
            section.kind === 'invites' && section.data.length === 0
              ? <Text style={s.emptySection}>Nothing is waiting. When someone invites you to a room or a workspace, it shows up here.</Text> :
            section.kind === 'rooms' && section.data.length === 0 && !section.collapsible
              ? <Text style={s.emptySection}>{query ? 'No rooms match.' : section.spaceId ? (section.admin ? 'No rooms in this workspace yet — tap "Manage" to add some.' : 'Nothing shared with you in this workspace yet.') : section.home ? `The rest of your rooms are in workspaces. Pick one ${rail ? 'on the left' : 'above'}.` : 'No rooms here yet.'}</Text> : null}
          ListEmptyComponent={<View style={s.center}><Text style={s.empty}>No rooms yet. Ask your admin to invite you.</Text></View>}
        />
      )}
    </SafeAreaView>
  );
}

/** Mail-style swipe: drag a row either way to reveal the action; past the threshold it fires and the row snaps back. */
function SwipeRow({ archived, onSwipe, children }: { archived: boolean; onSwipe: () => void; children: React.ReactNode }) {
  const s = useStyles();
  const ref = useRef<SwipeableMethods>(null);
  const panel = (side: 'left' | 'right') => () => (
    <View style={[s.swipePanel, archived ? s.swipePanelKeep : s.swipePanelArchive, { alignItems: side === 'left' ? 'flex-start' : 'flex-end' }]}>
      <Text style={[s.swipeText, archived ? s.swipeTextKeep : s.swipeTextArchive]}>{archived ? '↑ Unarchive' : '↓ Archive'}</Text>
    </View>
  );
  return (
    <ReanimatedSwipeable
      ref={ref} friction={1.6} leftThreshold={72} rightThreshold={72} overshootLeft={false} overshootRight={false}
      renderLeftActions={panel('left')} renderRightActions={panel('right')}
      onSwipeableWillOpen={() => { ref.current?.close(); onSwipe(); }}
    >
      {children}
    </ReanimatedSwipeable>
  );
}

/** An open approval gate, answerable right here. */
function GateCard({ gate, onOpen }: { gate: Gate; onOpen: () => void }) {
  const theme = useTheme(); const s = useStyles();
  const { room, event } = gate;
  const who = senderName(room, event.sender);
  // A card says what it is about in its title and carries the thing to approve; a Hermes gate is its own text.
  const card = event.card;
  const said = card ? [card.title, card.body || card.subtitle].filter(Boolean).join(': ') : event.body;
  const snippet = String(said).replace(/[`*_>#]/g, '').replace(/\s+/g, ' ').trim().slice(0, 180);
  const [asking, setAsking] = useState(false);
  const act = (c: GateChoice, note?: string) => answerGate(room.id, event, c, note).catch(() => {});
  return (
    // The card itself opens the request; the buttons answer it without opening.
    <Pressable onPress={onOpen} accessibilityRole="button" accessibilityLabel={`Open ${card?.title || room.name}`} style={({ pressed }) => [s.gcard, pressed && { backgroundColor: theme.surface2 }]}>
      <View style={s.gtop}>
        <Avatar name={who} mxc={room.memberAvatars[event.sender]} kind="agent" size={30} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={s.gwho} numberOfLines={1}>{who} <Text style={s.gin}>in {room.name}</Text></Text>
          <Text style={s.gtime}>{fmtTime(event.ts)}</Text>
        </View>
        <Text style={s.gchev}>›</Text>
      </View>
      <Text style={s.gsnippet} numberOfLines={3}>{snippet}</Text>
      <View style={s.gacts}>
        <Pressable onPress={() => act('approve')} style={({ pressed }) => [s.gbtn, s.gbtnApprove, pressed && { opacity: 0.6 }]}>
          <Text style={[s.gbtnText, { color: theme.approve }]}>Approve</Text>
        </Pressable>
        <Pressable onPress={() => setAsking(true)} style={({ pressed }) => [s.gbtn, s.gbtnOpen, pressed && { opacity: 0.6 }]}>
          <Text style={s.gbtnText}>Revise</Text>
        </Pressable>
        <Pressable onPress={() => act('deny')} style={({ pressed }) => [s.gbtn, s.gbtnDeny, pressed && { opacity: 0.6 }]}>
          <Text style={[s.gbtnText, { color: theme.deny }]}>Deny</Text>
        </Pressable>
      </View>
      <ReviseSheet visible={asking} about={card?.title || who} onCancel={() => setAsking(false)} onSend={(note) => { setAsking(false); act('revise', note); }} />
    </Pressable>
  );
}

function InviteRow({ invite, isAgent, onAccepted }: { invite: Invite; isAgent: (uid: string) => boolean; onAccepted: (room?: Room) => void }) {
  const theme = useTheme(); const s = useStyles();
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    // A workspace invitation also lands you in its shared rooms, like Slack's default channels.
    try { await (invite.isSpace ? joinWorkspace(invite.id) : acceptInvite(invite.id)); onAccepted(); }
    catch (e: any) { Alert.alert('Could not join', e?.message); }
    finally { setBusy(false); }
  };
  const decline = () => declineInvite(invite.id).catch((e) => Alert.alert('Could not decline', e?.message));
  const kind = invite.isSpace ? 'space' : invite.isDm ? (isAgent(invite.inviter) ? 'agent' : 'human') : 'room';
  return (
    <View style={s.icard}>
      <Avatar name={invite.isDm ? invite.inviterName : invite.name} kind={kind} size={42} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={s.rowName} numberOfLines={1}>{invite.name}</Text>
        <Text style={s.preview} numberOfLines={1}>{invite.inviterName} invited you{invite.isSpace ? ' to a workspace' : ''}</Text>
      </View>
      {busy ? <ActivityIndicator color={theme.accent} /> : (
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <Pressable onPress={accept} style={[s.gbtn, s.gbtnApprove]}><Text style={[s.gbtnText, { color: theme.approve }]}>Join</Text></Pressable>
          <Pressable onPress={decline} style={[s.gbtn, s.gbtnDeny]} accessibilityLabel="Decline"><Text style={[s.gbtnText, { color: theme.deny }]}>✕</Text></Pressable>
        </View>
      )}
    </View>
  );
}

function RoomRow({ room, me, isAgent, humanBadge, active, onPress, onLongPress }: {
  room: Room; me: string | null; isAgent: (uid: string) => boolean; humanBadge: boolean; active?: boolean; onPress: () => void; onLongPress: () => void;
}) {
  const theme = useTheme(); const s = useStyles();
  const last = room.last;
  let preview = '';
  if (room.typing.length) preview = `${senderName(room, room.typing[0])} is typing…`;
  else if (last) {
    const who = last.sender === me ? 'You' : senderName(room, last.sender);
    const what = last.kind === 'image' ? '📷 Photo' : last.kind === 'file' ? '📎 File' : last.kind === 'encrypted' ? '🔒 Encrypted'
      : last.card?.title ? `▤ ${last.card.title}` : last.body.replace(/[`*_>#]/g, '').replace(/\s+/g, ' ').trim();
    preview = last.thread ? `${who} started a new conversation` : `${who}: ${what}`;
  } else if (room.topic) preview = room.topic;
  const total = room.unread + threadUnreadTotal(room);
  const unread = total > 0;
  return (
    <Pressable onPress={onPress} onLongPress={onLongPress} delayLongPress={350} style={({ pressed }) => [s.row, active && s.rowActive, pressed && { backgroundColor: theme.surface2 }]}>
      <RoomAvatar room={room} me={me} isAgent={isAgent} size={42} humanBadge={humanBadge} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={s.rowTop}>
          <Text style={[s.rowName, unread && { fontWeight: '800' }]} numberOfLines={1}>{room.name}</Text>
          {last ? <Text style={s.time}>{fmtTime(last.ts)}</Text> : null}
        </View>
        <View style={s.rowTop}>
          {preview ? <Text style={[s.preview, unread && { color: theme.ink }, room.typing.length > 0 && { color: theme.accent, fontStyle: 'italic' }]} numberOfLines={1}>{preview}</Text> : <View style={{ flex: 1 }} />}
          {unread ? <View style={s.badge}><Text style={s.badgeText}>{total > 99 ? '99+' : total}</Text></View> : null}
        </View>
      </View>
    </Pressable>
  );
}

function fmtTime(ts: number) {
  const d = new Date(ts); const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.bg },
  header: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingHorizontal: 16, paddingVertical: 12 },
  hTitle: { color: theme.ink, fontSize: 20, fontWeight: '700', fontFamily: theme.mono, letterSpacing: 0.5 },
  hSub: { color: theme.faint, fontSize: 12, marginTop: 2, fontFamily: theme.mono },
  newBtn: { backgroundColor: theme.accent, borderRadius: radius.pill, paddingHorizontal: 12, paddingVertical: 6 },
  newBtnText: { color: theme.accentInk, fontWeight: '800', fontSize: 13 },
  gear: { width: 34, height: 34, borderRadius: radius.md, backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, alignItems: 'center', justifyContent: 'center' },
  gearText: { color: theme.ink, fontSize: 18, lineHeight: 20 },
  searchWrap: { paddingHorizontal: 12, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: theme.border },
  search: { backgroundColor: theme.surface, borderRadius: radius.md, color: theme.ink, fontSize: 15, paddingHorizontal: 12, paddingVertical: 9 },
  banner: { backgroundColor: theme.surface, paddingVertical: 6, alignItems: 'center', borderBottomWidth: 1, borderBottomColor: theme.border },
  bannerText: { color: theme.muted, fontSize: 12 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 12 },
  sectionRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 6, paddingTop: 14, paddingBottom: 8 },
  section: { color: theme.faint, fontSize: 11, fontWeight: '700', letterSpacing: 0.8, textTransform: 'uppercase', fontFamily: theme.mono, flexShrink: 1 },
  chev: { color: theme.faint, fontSize: 12 },
  manage: { color: theme.accent, fontSize: 12, fontWeight: '700', fontFamily: theme.mono },
  time: { color: theme.faint, fontSize: 11, fontFamily: theme.mono },
  elsewhere: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingHorizontal: 12, paddingVertical: 10, borderRadius: radius.md, backgroundColor: theme.agentBg, borderWidth: 1, borderColor: theme.agentBorder },
  elsewhereText: { color: theme.ink, fontSize: 13, fontWeight: '600', flex: 1, minWidth: 0 },
  emptySection: { color: theme.faint, fontSize: 13, paddingHorizontal: 12, paddingVertical: 8 },
  // swipe panels behind a row
  swipePanel: { flex: 1, justifyContent: 'center', paddingHorizontal: 22, borderRadius: radius.md, marginVertical: 1 },
  swipePanelArchive: { backgroundColor: theme.accentBg },
  swipePanelKeep: { backgroundColor: theme.approveBg },
  swipeText: { fontWeight: '800', fontSize: 13.5, fontFamily: theme.mono },
  swipeTextArchive: { color: theme.accent },
  swipeTextKeep: { color: theme.approve },
  // gate cards
  gcard: { backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.agentBorder, borderRadius: radius.lg, padding: 12, marginBottom: 10, gap: 8 },
  gtop: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  gwho: { color: theme.ink, fontSize: 14, fontWeight: '700' },
  gchev: { color: theme.faint, fontSize: 22, lineHeight: 24, fontWeight: '600' },
  gin: { color: theme.faint, fontWeight: '500' },
  gtime: { color: theme.faint, fontSize: 11, marginTop: 1 },
  gsnippet: { color: theme.muted, fontSize: 13.5, lineHeight: 19 },
  gacts: { flexDirection: 'row', gap: 6, marginTop: 2, flexWrap: 'wrap' }, // four buttons: they must fit a 340px sidebar and a narrow phone
  gbtn: { paddingHorizontal: 14, paddingVertical: 9, borderRadius: radius.md, borderWidth: 1 },
  gbtnApprove: { backgroundColor: theme.approveBg, borderColor: theme.approveBorder },
  gbtnDeny: { backgroundColor: 'transparent', borderColor: theme.denyBorder },
  gbtnOpen: { backgroundColor: theme.surface2, borderColor: theme.border },
  gbtnText: { color: theme.ink, fontWeight: '700', fontSize: 13.5 },
  // invites
  icard: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: radius.lg, padding: 12, marginBottom: 8 },
  // rooms
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: radius.md, backgroundColor: theme.bg },
  rowActive: { backgroundColor: theme.surface2 }, // desktop web: the room open in the right pane
  rowTop: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  rowName: { color: theme.ink, fontSize: 16, fontWeight: '600', flex: 1 },
  preview: { color: theme.muted, fontSize: 13.5, marginTop: 2, flex: 1 },
  badge: { backgroundColor: theme.accent, borderRadius: radius.pill, minWidth: 20, paddingHorizontal: 6, paddingVertical: 2, alignItems: 'center' },
  badgeText: { color: theme.accentInk, fontSize: 11, fontWeight: '800' },
  empty: { color: theme.faint, fontSize: 14, textAlign: 'center' },
}));
